import * as React from "react";

/**
 * The toast context and its hook. `ToastProvider` is next door in `Toast.tsx`.
 *
 * ── Why this is a file of its own ───────────────────────────────────────────
 *
 * Same reason as `confirm-context.ts`, and the same bug behind it. React Fast
 * Refresh only treats a module as a refresh boundary when every export is a
 * component; a module exporting a provider *and* a hook *and* a module-level
 * `createContext` gets re-evaluated instead, minting a fresh context object
 * while consumers that did not re-render keep the old one. The symptom is
 * "must be used inside a Provider" on a tree that plainly has one, cleared by
 * a full reload - which is what makes it look like a phantom.
 *
 * ── What a toast is for, and what it is not ────────────────────────────────
 *
 * A toast reports that something the user asked for **succeeded**, at a moment
 * when the thing they were looking at is about to change or has just closed.
 * "Invitation sent to alex@acme.com" after the dialog dismisses is the shape:
 * the confirmation outlives the surface that requested it.
 *
 * **Failures do not belong here, and the reason is in `Feedback.tsx`.** A toast
 * auto-dismisses; an error the user has to act on must stay on screen until
 * they deal with it. Every form in this product renders its failure through
 * `FormMessage`, next to the control that produced it, and every list row has
 * `.row-error` for the same purpose. `error()` exists below for the narrow case
 * of an action with genuinely nowhere to put a message, and reaching for it
 * when the call site has a form is how a product ends up reporting failures
 * that vanish after four seconds.
 *
 * ── Not a replacement for a refetch ────────────────────────────────────────
 *
 * A toast is not evidence. It says the request came back 2xx; the list under it
 * still has to be revalidated, because the toast is a sentence and the table is
 * the record. Raise it *beside* `mutate()`, never instead of one.
 */
export type ToastTone = "success" | "error" | "info";

export interface ToastOptions {
  /**
   * A second line under the message. For the detail that makes the sentence
   * verifiable - an address, a site name, a count - not for restating it.
   */
  description?: string;
}

export interface ToastApi {
  success: (message: string, options?: ToastOptions) => void;
  /** Read the header above before using this. It is almost never the right call. */
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
}

export const ToastContext = React.createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const toast = React.useContext(ToastContext);
  if (!toast) throw new Error("useToast must be used inside <ToastProvider>");
  return toast;
}
